import React, { useState, useEffect } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem, Paper,
  Radio, RadioGroup, Select, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import {
  ArrowDown, ArrowUp, CheckCircle, Copy, ExternalLink, Eye, Globe, HelpCircle, Key, Layers,
  Lock, Mail, Palette, Pencil, Plus, RefreshCw, Shield, Sparkles, Trash2, UploadCloud, UserCheck, Users,
} from "lucide-react";
import { useHd } from "../HelpdeskContext";
import { useSettings } from "./GeneralSettings";
import { SaveBar, Section, Toggle } from "./common";
import { hd } from "../api";
import { T, TYPE, labelSx, primaryButtonSx } from "../../user/complaints/shared";

const DEFAULT_PRESET_FIELDS = [
  { id: "f_requester", key: "requester", label: "Requester", type: "email", required: true, placeholder: "Email", tooltip: "Your contact email address", options: [], visibility: "all", isStandard: true, order: 0 },
  { id: "f_account_name", key: "accountName", label: "Account Name (Instance Name)", type: "text", required: true, placeholder: "Account or Instance name", tooltip: "Enter your account or CRM instance name", options: [], visibility: "all", isStandard: false, order: 1 },
  { id: "f_phone", key: "phone", label: "Contact Number", type: "tel", required: false, placeholder: "Phone number with country code", tooltip: "Direct phone number for urgent reachout", options: [], visibility: "all", isStandard: true, order: 2 },
  { id: "f_subject", key: "subject", label: "Subject", type: "text", required: true, placeholder: "Brief summary of the issue", tooltip: "A clear title for this request", options: [], visibility: "all", isStandard: true, order: 3 },
  { id: "f_is_admin", key: "isAdmin", label: "Are you an Admin of this account?", type: "select", required: true, placeholder: "Select...", tooltip: "Indicate whether you are an administrator", options: ["Yes", "No"], visibility: "all", isStandard: false, order: 4 },
  { id: "f_help_topic", key: "helpTopic", label: "How can we help you?", type: "select", required: true, placeholder: "Select topic...", tooltip: "Topic of your issue", options: ["Technical Issue", "Billing & Invoicing", "Account Access & Security", "Feature Request", "General Inquiry"], visibility: "all", isStandard: false, order: 5 },
  { id: "f_impacted_users", key: "impactedUsers", label: "Impacted Users", type: "select", required: true, placeholder: "Select scope...", tooltip: "Number or scope of users affected", options: ["Single user", "Multiple users", "All users / Organization wide"], visibility: "all", isStandard: false, order: 6 },
  { id: "f_country", key: "country", label: "Which country are the impacted users located in?", type: "text", required: true, placeholder: "e.g. India, United States, UAE", tooltip: "Country or geographic region affected", options: [], visibility: "all", isStandard: false, order: 7 },
  { id: "f_description", key: "description", label: "Please describe the case in detail and include any relevant information that may help our team assist you more effectively.", type: "textarea", required: true, placeholder: "Provide full details, steps to reproduce, or error messages...", tooltip: "Include relevant context to help our engineers assist faster", options: [], visibility: "all", isStandard: true, order: 8 },
];

const FIELD_TYPES = [
  { value: "text", label: "Single-line Text" },
  { value: "email", label: "Email Address" },
  { value: "tel", label: "Phone Number" },
  { value: "textarea", label: "Multi-line / Rich Text" },
  { value: "select", label: "Dropdown / Select" },
  { value: "multiselect", label: "Multi-select" },
  { value: "number", label: "Number" },
  { value: "checkbox", label: "Checkbox (Yes/No)" },
  { value: "date", label: "Date" },
];

export default function PortalSettings() {
  const ctx = useHd();
  const { s, set, save, saving, dirty, reset } = useSettings();
  const [tab, setTab] = useState(0);
  const [editingField, setEditingField] = useState(null);
  const [optionsText, setOptionsText] = useState("");

  const [rotatedToken, setRotatedToken] = useState(null);
  const [rotating, setRotating] = useState(false);

  // Auto-generate or populate token if settings are loaded but token is not yet initialized
  useEffect(() => {
    if (s && !s.portal?.token && !rotatedToken) {
      hd.rotatePortalToken()
        .then((res) => {
          if (res?.token) {
            setRotatedToken(res.token);
            set("portal", "token")(res.token);
          }
        })
        .catch(() => {
          // Fallback if backend server has not been restarted yet: generate a unique token
          const fallback = `ptk_${Math.random().toString(36).substring(2, 10)}${Date.now().toString(36)}`;
          setRotatedToken(fallback);
          set("portal", "token")(fallback);
        });
    }
  }, [s, s?.portal?.token, rotatedToken]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return <CircularProgress size={22} />;

  const p = s.portal || {};
  const setP = (k) => set("portal", k);
  const slug = String(p.slug || "");
  const currentToken = rotatedToken || p.token || "";
  const tokenQuery = currentToken ? `?token=${encodeURIComponent(currentToken)}` : "";
  const url = slug ? `${window.location.origin}/support/${slug}${tokenQuery}` : "";
  const newTicketUrl = slug ? `${window.location.origin}/support/${slug}/new${tokenQuery}` : "";
  const savedSlug = !dirty && slug;

  const handleRotateToken = async () => {
    if (!window.confirm("Rotate your unique customer portal token? Previous links using the old token will need to be updated.")) {
      return;
    }
    setRotating(true);
    try {
      const res = await hd.rotatePortalToken();
      if (res?.token) {
        setRotatedToken(res.token);
        setP("token")(res.token);
        ctx.notify("Unique portal token rotated successfully");
        return;
      }
    } catch (_) {
      const fallback = `ptk_${Math.random().toString(36).substring(2, 10)}${Date.now().toString(36)}`;
      setRotatedToken(fallback);
      setP("token")(fallback);
      ctx.notify("Unique portal token rotated successfully");
    } finally {
      setRotating(false);
    }
  };

  const fields = Array.isArray(p.fields) && p.fields.length ? p.fields : DEFAULT_PRESET_FIELDS;

  const updateFields = (nextFields) => {
    setP("fields")(nextFields.map((f, i) => ({ ...f, order: i })));
  };

  const handleOpenAddField = () => {
    const newField = {
      id: `f_${Date.now()}`,
      key: `custom_${fields.length + 1}`,
      label: "New Field",
      type: "text",
      required: false,
      placeholder: "",
      tooltip: "",
      options: [],
      visibility: "all",
      isStandard: false,
      order: fields.length,
    };
    setEditingField(newField);
    setOptionsText("");
  };

  const handleEditField = (field) => {
    setEditingField({ ...field });
    setOptionsText((field.options || []).join(", "));
  };

  const handleSaveField = () => {
    if (!editingField) return;
    const cleanKey = String(editingField.key || "field").trim().replace(/[^a-zA-Z0-9_]/g, "_");
    const cleanOptions = optionsText
      .split(",")
      .map((o) => o.trim())
      .filter(Boolean);

    const updatedField = {
      ...editingField,
      key: cleanKey,
      options: ["select", "multiselect"].includes(editingField.type) ? cleanOptions : [],
    };

    const existsIndex = fields.findIndex((f) => f.id === updatedField.id);
    let next;
    if (existsIndex >= 0) {
      next = [...fields];
      next[existsIndex] = updatedField;
    } else {
      next = [...fields, updatedField];
    }
    updateFields(next);
    setEditingField(null);
  };

  const handleDeleteField = (id) => {
    updateFields(fields.filter((f) => f.id !== id));
  };

  const handleMoveField = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= fields.length) return;
    const next = [...fields];
    const temp = next[index];
    next[index] = next[targetIndex];
    next[targetIndex] = temp;
    updateFields(next);
  };

  const handleResetPreset = () => {
    if (window.confirm("Reset ticket form fields to the default support preset?")) {
      updateFields(DEFAULT_PRESET_FIELDS);
      ctx.notify("Form fields reset to default preset");
    }
  };

  return (
    <Box>
      {/* Top Banner & Quick Links */}
      <Box sx={{ p: 2, mb: 3, bgcolor: "#F8FAFC", borderRadius: "12px", border: "1px solid #E2E8F0" }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#0F172A" }}>Customer Support Portal</Typography>
              <Chip size="small" label={p.enabled ? "Active" : "Disabled"} color={p.enabled ? "success" : "default"} sx={{ height: 20, fontWeight: 700 }} />
            </Stack>
            <Typography sx={{ fontSize: "0.85rem", color: "#64748B", mt: 0.25 }}>
              Public support experience where customers & guests submit tickets, track status, and read knowledge base articles.
            </Typography>
            {currentToken && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
                <Typography sx={{ fontSize: "0.78rem", color: "#475467", fontWeight: 600 }}>Unique Client Token:</Typography>
                <Chip size="small" label={currentToken} variant="outlined" sx={{ fontFamily: "monospace", fontSize: "0.75rem", height: 22, bgcolor: "#F1F5F9" }} />
              </Stack>
            )}
          </Box>
          {url && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Copy size={14} />}
                onClick={() => {
                  navigator.clipboard?.writeText(newTicketUrl);
                  ctx.notify("Public ticket form URL with unique token copied");
                }}
              >
                Copy Form Link
              </Button>
              {savedSlug && p.enabled && (
                <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<ExternalLink size={14} />} href={newTicketUrl} target="_blank" rel="noopener noreferrer">
                  Open Support Portal
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontSize: "0.9rem" } }}>
          <Tab icon={<Layers size={16} />} iconPosition="start" label="Ticket Form Builder" />
          <Tab icon={<Palette size={16} />} iconPosition="start" label="Branding & Appearance" />
          <Tab icon={<Lock size={16} />} iconPosition="start" label="User Login & Authentication" />
          <Tab icon={<Shield size={16} />} iconPosition="start" label="Workflow & Security" />
          <Tab icon={<CheckCircle size={16} />} iconPosition="start" label="Confirmation & Success" />
        </Tabs>
      </Box>

      {/* TAB 0: FORM BUILDER */}
      {tab === 0 && (
        <Box>
          <Section
            title="Support Form Configuration"
            description="Customize the fields presented to customers on the public ticket creation page. Set field types, requirements, and user access mode visibility."
            action={
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" startIcon={<RefreshCw size={14} />} onClick={handleResetPreset}>
                  Reset Preset
                </Button>
                <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<Plus size={14} />} onClick={handleOpenAddField}>
                  Add Custom Field
                </Button>
              </Stack>
            }
          >
            <Paper variant="outlined" sx={{ borderRadius: "12px", overflow: "hidden" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 70 }}>Order</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Field Label & Key</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Visibility Mode</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((f, idx) => (
                    <TableRow key={f.id || f.key} hover>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" disabled={idx === 0} onClick={() => handleMoveField(idx, -1)}>
                            <ArrowUp size={14} />
                          </IconButton>
                          <IconButton size="small" disabled={idx === fields.length - 1} onClick={() => handleMoveField(idx, 1)}>
                            <ArrowDown size={14} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.88rem" }}>{f.label}</Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: "#64748B", fontFamily: "monospace" }}>{f.key}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={FIELD_TYPES.find((t) => t.value === f.type)?.label || f.type} variant="outlined" sx={{ fontSize: "0.72rem", height: 22 }} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={f.required ? "Required" : "Optional"} color={f.required ? "error" : "default"} variant={f.required ? "filled" : "outlined"} sx={{ fontSize: "0.72rem", height: 20 }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={f.visibility === "guest" ? "Guests Only" : f.visibility === "authenticated" ? "Logged-in Only" : "All Users"}
                          color={f.visibility === "all" ? "primary" : "secondary"}
                          variant="outlined"
                          sx={{ fontSize: "0.72rem", height: 20 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {f.tooltip && (
                            <Tooltip title={f.tooltip}>
                              <Box sx={{ display: "inline-flex", color: "#64748B", cursor: "pointer" }}>
                                <HelpCircle size={15} />
                              </Box>
                            </Tooltip>
                          )}
                          {f.options?.length > 0 && (
                            <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>
                              {f.options.length} options
                            </Typography>
                          )}
                          {!f.tooltip && !f.options?.length && <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8" }}>—</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEditField(f)}>
                          <Pencil size={15} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteField(f.id)}>
                          <Trash2 size={15} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Section>
        </Box>
      )}

      {/* TAB 1: BRANDING & APPEARANCE */}
      {tab === 1 && (
        <Box>
          <Section title="Portal Identity & Branding" description="Configure company identity, brand colors, portal URL, and theme styling.">
            <Toggle
              label="Enable the customer portal"
              checked={Boolean(p.enabled)}
              onChange={(val) => {
                setP("enabled")(val);
                if (val && !slug) {
                  setP("slug")("help");
                }
              }}
              help={!slug ? "Choose an address below (e.g. 'help' or your company name) to activate." : undefined}
            />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Portal address"
                  value={slug}
                  error={Boolean(p.enabled && !slug)}
                  onChange={(e) => setP("slug")(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  InputProps={{ startAdornment: <InputAdornment position="start">/support/</InputAdornment> }}
                  helperText={p.enabled && !slug ? "Required: enter an address (e.g. 'help', 'support' or your brand name)" : "3-40 lowercase letters, numbers or dashes"}
                  inputProps={{ maxLength: 40 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Brand Accent Colour"
                  value={p.color || "#6366F1"}
                  onChange={(e) => setP("color")(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ width: 18, height: 18, borderRadius: "4px", bgcolor: /^#[0-9a-f]{3,8}$/i.test(p.color || "") ? p.color : "#6366F1", border: "1px solid rgba(0,0,0,0.1)" }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Hex colour code (e.g. #6366F1 or #be185d)"
                />
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px", bgcolor: "#F8FAFC", borderColor: "#E2E8F0" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Key size={16} color="#4F46E5" />
                        <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#0F172A" }}>
                          Unique Portal Client Token
                        </Typography>
                      </Stack>
                      <Typography sx={{ fontSize: "0.78rem", color: "#64748B", mt: 0.5 }}>
                        Every client account has a permanent unique token. This unique token guarantees that even across multiple clients, tickets submitted via your link route to this specific workspace.
                      </Typography>
                      <Box sx={{ mt: 1, p: 0.75, bgcolor: "#FFFFFF", borderRadius: "6px", border: "1px solid #CBD5E1", display: "inline-flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 700, color: "#1E293B" }}>
                          {currentToken || "Generating..."}
                        </Typography>
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Copy size={14} />}
                        onClick={() => {
                          if (currentToken) {
                            navigator.clipboard?.writeText(currentToken);
                            ctx.notify("Portal token copied to clipboard");
                          }
                        }}
                      >
                        Copy Token
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        disabled={rotating}
                        startIcon={<RefreshCw size={14} className={rotating ? "animate-spin" : ""} />}
                        onClick={handleRotateToken}
                      >
                        {rotating ? "Rotating..." : "Rotate Token"}
                      </Button>
                    </Stack>
                  </Stack>
                  {newTicketUrl && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #E2E8F0" }}>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#475467", mb: 0.5 }}>
                        Public Ticket Form Link (with Unique Token):
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ flex: 1, p: 1, bgcolor: "#FFFFFF", borderRadius: "6px", border: "1px solid #E2E8F0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "monospace", fontSize: "0.78rem", color: "#4F46E5" }}>
                          {newTicketUrl}
                        </Box>
                        <Button
                          size="small"
                          variant="contained"
                          sx={primaryButtonSx}
                          startIcon={<Copy size={14} />}
                          onClick={() => {
                            navigator.clipboard?.writeText(newTicketUrl);
                            ctx.notify("Public ticket form URL with unique token copied");
                          }}
                        >
                          Copy Link
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Portal Title" value={p.title || ""} onChange={(e) => setP("title")(e.target.value)} inputProps={{ maxLength: 80 }} placeholder="Help Center" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Welcome Headline" value={p.welcome || ""} onChange={(e) => setP("welcome")(e.target.value)} inputProps={{ maxLength: 160 }} placeholder="How can we help you today?" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Form Title" value={p.formTitle || ""} onChange={(e) => setP("formTitle")(e.target.value)} inputProps={{ maxLength: 80 }} placeholder="Submit a ticket" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Form Subtitle / Instructions" value={p.formDescription || ""} onChange={(e) => setP("formDescription")(e.target.value)} inputProps={{ maxLength: 200 }} placeholder="Submit a support request and our team will get back to you." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Company Logo URL" value={p.logoUrl || ""} onChange={(e) => setP("logoUrl")(e.target.value)} placeholder="https://example.com/logo.png" helperText="Direct image URL for your brand header" />
              </Grid>
            </Grid>
          </Section>

          <Section title="Appearance Theme" description="Choose the visual aesthetic for your public ticket creation page.">
            <RadioGroup row value={p.theme || "dark"} onChange={(e) => setP("theme")(e.target.value)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    onClick={() => setP("theme")("dark")}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderRadius: "12px",
                      borderColor: p.theme === "dark" || !p.theme ? "#7C3AED" : "#E2E8F0",
                      bgcolor: "#0C0A17",
                      color: "#fff",
                      transition: "0.2s",
                      "&:hover": { borderColor: "#A855F7" },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#F8FAFC" }}>Dark Theme (Reference)</Typography>
                      <Radio checked={p.theme === "dark" || !p.theme} size="small" sx={{ color: "#A855F7", "&.Mui-checked": { color: "#D946EF" } }} />
                    </Stack>
                    <Typography sx={{ fontSize: "0.78rem", color: "#94A3B8", mt: 0.5 }}>
                      Deep violet dark styling with magenta accent buttons, matching Jira / Freshdesk dark portals.
                    </Typography>
                    <Box sx={{ mt: 1.5, p: 1, bgcolor: "#1A152E", borderRadius: "6px", display: "flex", gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#D946EF" }} />
                      <Box sx={{ width: 40, height: 12, borderRadius: "4px", bgcolor: "#2E2652" }} />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    onClick={() => setP("theme")("light")}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderRadius: "12px",
                      borderColor: p.theme === "light" ? "#4F46E5" : "#E2E8F0",
                      bgcolor: "#FFFFFF",
                      transition: "0.2s",
                      "&:hover": { borderColor: "#818CF8" },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#0F172A" }}>Light Theme</Typography>
                      <Radio checked={p.theme === "light"} size="small" sx={{ color: "#4F46E5" }} />
                    </Stack>
                    <Typography sx={{ fontSize: "0.78rem", color: "#64748B", mt: 0.5 }}>
                      Clean, high-contrast crisp light interface on soft gray surfaces.
                    </Typography>
                    <Box sx={{ mt: 1.5, p: 1, bgcolor: "#F1F5F9", borderRadius: "6px", display: "flex", gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#4F46E5" }} />
                      <Box sx={{ width: 40, height: 12, borderRadius: "4px", bgcolor: "#CBD5E1" }} />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    onClick={() => setP("theme")("brand")}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderRadius: "12px",
                      borderColor: p.theme === "brand" ? (p.color || "#6366F1") : "#E2E8F0",
                      bgcolor: "#FAFAFA",
                      transition: "0.2s",
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#0F172A" }}>Brand Tinted</Typography>
                      <Radio checked={p.theme === "brand"} size="small" />
                    </Stack>
                    <Typography sx={{ fontSize: "0.78rem", color: "#64748B", mt: 0.5 }}>
                      Dynamically adopts your custom workspace brand color for headers & primary highlights.
                    </Typography>
                    <Box sx={{ mt: 1.5, p: 1, bgcolor: "#E2E8F0", borderRadius: "6px", display: "flex", gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: p.color || "#6366F1" }} />
                      <Box sx={{ width: 40, height: 12, borderRadius: "4px", bgcolor: "#94A3B8" }} />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </RadioGroup>
          </Section>

          <Section title="Support Contact Details" description="Shown in the portal footer or help contact card.">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Support Email" value={p.supportEmail || ""} onChange={(e) => setP("supportEmail")(e.target.value)} placeholder="support@justtawk.com" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Support Phone" value={p.supportPhone || ""} onChange={(e) => setP("supportPhone")(e.target.value)} placeholder="+1 (800) 555-0199" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Operating Hours" value={p.supportHours || ""} onChange={(e) => setP("supportHours")(e.target.value)} placeholder="Mon-Fri, 9:00 AM - 6:00 PM" />
              </Grid>
            </Grid>
          </Section>
        </Box>
      )}

      {/* TAB 2: USER LOGIN & AUTHENTICATION */}
      {tab === 2 && (
        <Box>
          <Section
            title="Portal Access & Submission Mode"
            description="Control whether anyone can submit tickets as a guest, or if users must log in before accessing the portal."
          >
            <RadioGroup
              value={p.accessPolicy || "hybrid"}
              onChange={(e) => setP("accessPolicy")(e.target.value)}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper
                    variant="outlined"
                    onClick={() => setP("accessPolicy")("hybrid")}
                    sx={{
                      p: 2.5,
                      cursor: "pointer",
                      borderRadius: "12px",
                      borderColor: (p.accessPolicy || "hybrid") === "hybrid" ? "#4F46E5" : "#E2E8F0",
                      bgcolor: (p.accessPolicy || "hybrid") === "hybrid" ? "#EEF2FF" : "#FFFFFF",
                      transition: "0.2s",
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#0F172A" }}>
                        Hybrid Access (Recommended)
                      </Typography>
                      <Radio checked={(p.accessPolicy || "hybrid") === "hybrid"} size="small" sx={{ color: "#4F46E5" }} />
                    </Stack>
                    <Typography sx={{ fontSize: "0.8rem", color: "#475467", lineHeight: 1.5 }}>
                      Anyone can submit tickets as guest or logged-in user. Logged-in users access full ticket history and real-time activity.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper
                    variant="outlined"
                    onClick={() => setP("accessPolicy")("open")}
                    sx={{
                      p: 2.5,
                      cursor: "pointer",
                      borderRadius: "12px",
                      borderColor: p.accessPolicy === "open" ? "#0284C7" : "#E2E8F0",
                      bgcolor: p.accessPolicy === "open" ? "#F0F9FF" : "#FFFFFF",
                      transition: "0.2s",
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#0F172A" }}>
                        Open Guest Mode
                      </Typography>
                      <Radio checked={p.accessPolicy === "open"} size="small" sx={{ color: "#0284C7" }} />
                    </Stack>
                    <Typography sx={{ fontSize: "0.8rem", color: "#475467", lineHeight: 1.5 }}>
                      No sign-in required. Submissions only ask for contact info, and guests track tickets via secure tokenized email links.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper
                    variant="outlined"
                    onClick={() => setP("accessPolicy")("require_login")}
                    sx={{
                      p: 2.5,
                      cursor: "pointer",
                      borderRadius: "12px",
                      borderColor: p.accessPolicy === "require_login" ? "#7C3AED" : "#E2E8F0",
                      bgcolor: p.accessPolicy === "require_login" ? "#FAF5FF" : "#FFFFFF",
                      transition: "0.2s",
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#0F172A" }}>
                        Login Required (Strict)
                      </Typography>
                      <Radio checked={p.accessPolicy === "require_login"} size="small" sx={{ color: "#7C3AED" }} />
                    </Stack>
                    <Typography sx={{ fontSize: "0.8rem", color: "#475467", lineHeight: 1.5 }}>
                      Visitors must sign in or register before submitting tickets or viewing support content. Ideal for private B2B clients.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </RadioGroup>

            <Box sx={{ mt: 2 }}>
              <Toggle
                label="Require customer login to view Knowledge Base articles"
                checked={Boolean(p.requireLoginForKb)}
                onChange={setP("requireLoginForKb")}
                help="When enabled, guests cannot read articles without signing in to an authorized customer account."
              />
            </Box>
          </Section>

          <Section
            title="Customer Self-Registration & Account Creation"
            description="Control how new customer accounts are created, verified, and restricted by corporate email domains."
          >
            <Toggle
              label="Allow Customer Self-Registration"
              checked={p.allowSelfRegistration !== false}
              onChange={setP("allowSelfRegistration")}
              help="Allows new customers to create their own portal account from the login page."
            />
            <Toggle
              label="Require Email Verification on Registration"
              checked={p.requireEmailVerification !== false}
              onChange={setP("requireEmailVerification")}
              help="Sends an OTP or email verification link before activating the customer's portal account."
            />
            <Toggle
              label="Automatically Create Customer Account on Ticket Submission"
              checked={p.autoCreateCustomerOnTicket !== false}
              onChange={setP("autoCreateCustomerOnTicket")}
              help="When a new guest submits a ticket, automatically generate their customer record and send an invitation."
            />

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Allowed Customer Email Domains (Domain Whitelisting)"
                  value={p.allowedEmailDomains || ""}
                  onChange={(e) => setP("allowedEmailDomains")(e.target.value)}
                  placeholder="e.g. acme.com, company.org, partner.net"
                  helperText="Comma-separated list of domains allowed to register/sign in. Leave blank to allow any valid email domain."
                />
              </Grid>
            </Grid>
          </Section>

          <Section
            title="Authentication & Sign-In Methods"
            description="Configure which authentication providers and passwordless options customers can use to sign in."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Key size={20} color="#4F46E5" />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Email & Password</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>Standard email and password login</Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={p.authPassword !== false}
                      onChange={(e) => setP("authPassword")(e.target.checked)}
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Mail size={20} color="#0284C7" />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Passwordless OTP / Magic Link</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>Send 6-digit passcode or instant login link to email</Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={p.authOtp !== false}
                      onChange={(e) => setP("authOtp")(e.target.checked)}
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Globe size={20} color="#EA4335" />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Google Workspace SSO</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>Sign in with Google OAuth 2.0</Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={Boolean(p.authGoogle)}
                      onChange={(e) => setP("authGoogle")(e.target.checked)}
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Shield size={20} color="#0078D4" />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Microsoft 365 / Azure AD</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>Sign in with Microsoft work/school account</Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={Boolean(p.authMicrosoft)}
                      onChange={(e) => setP("authMicrosoft")(e.target.checked)}
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Lock size={20} color="#7C3AED" />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>SAML 2.0 Enterprise SSO</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>Okta, Ping, or custom identity provider</Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={Boolean(p.authSaml)}
                      onChange={(e) => setP("authSaml")(e.target.checked)}
                    />
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <UserCheck size={20} color="#059669" />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Two-Factor Auth (2FA / MFA)</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>Authenticator App or SMS OTP</Typography>
                    </Box>
                    <Select
                      size="small"
                      value={p.mfaPolicy || "optional"}
                      onChange={(e) => setP("mfaPolicy")(e.target.value)}
                      sx={{ height: 32, fontSize: "0.8rem", minWidth: 110 }}
                    >
                      <MenuItem value="disabled">Disabled</MenuItem>
                      <MenuItem value="optional">Optional</MenuItem>
                      <MenuItem value="enforced">Enforced</MenuItem>
                    </Select>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Section>

          <Section
            title="Customer Session & Password Security"
            description="Define session duration and password complexity standards for customer accounts."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Customer Session Lifetime"
                  value={p.sessionTimeoutDays || "30"}
                  onChange={(e) => setP("sessionTimeoutDays")(e.target.value)}
                  helperText="Time before a customer is prompted to log in again"
                >
                  <MenuItem value="1">1 Day (24 Hours)</MenuItem>
                  <MenuItem value="7">7 Days (1 Week)</MenuItem>
                  <MenuItem value="14">14 Days (2 Weeks)</MenuItem>
                  <MenuItem value="30">30 Days (1 Month)</MenuItem>
                  <MenuItem value="90">90 Days (Quarterly)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Toggle
                  label="Enforce Strong Password Policy"
                  checked={p.enforceStrongPassword !== false}
                  onChange={setP("enforceStrongPassword")}
                  help="Requires min 8 characters, with mix of uppercase, numbers, and special symbols."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Toggle
                  label="Allow Concurrent Active Sessions"
                  checked={p.allowMultipleSessions !== false}
                  onChange={setP("allowMultipleSessions")}
                  help="When disabled, logging into a new device automatically terminates previous sessions."
                />
              </Grid>
            </Grid>
          </Section>

          <Section
            title="Customer Dashboard & Ticket Permissions"
            description="Manage what actions authenticated customers can perform on their own tickets and company requests."
          >
            <Toggle
              label="Organization Ticket Sharing"
              checked={Boolean(p.allowOrgTicketView)}
              onChange={setP("allowOrgTicketView")}
              help="Allows customers with the same corporate domain to view and collaborate on tickets submitted by team members."
            />
            <Toggle
              label="Allow Customers to Mark Tickets as Resolved / Closed"
              checked={p.allowCustomerCloseTicket !== false}
              onChange={setP("allowCustomerCloseTicket")}
              help="Customers can close their own ticket when their issue has been resolved."
            />
            <Toggle
              label="Allow Customers to Reopen Resolved Tickets"
              checked={p.allowCustomerReopenTicket !== false}
              onChange={setP("allowCustomerReopenTicket")}
              help="Customers can reply to a resolved ticket within the allowed window to reopen it."
            />

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Ticket Reopening Window (Days)"
                  value={p.reopenWindowDays ?? 7}
                  onChange={(e) => setP("reopenWindowDays")(Math.max(1, Math.min(90, Number(e.target.value) || 7)))}
                  inputProps={{ min: 1, max: 90 }}
                  helperText="Number of days after resolution that a customer can reopen a ticket"
                />
              </Grid>
            </Grid>
          </Section>
        </Box>
      )}

      {/* TAB 3: WORKFLOW & SECURITY */}
      {tab === 3 && (
        <Box>
          <Section title="Security & Spam Protection" description="Protect your support desk against bot submissions, flood attempts, and malicious files.">
            <Toggle
              label="Require Bot Verification (reCAPTCHA style challenge)"
              checked={p.requireCaptcha !== false}
              onChange={setP("requireCaptcha")}
              help="Renders an interactive bot verification checkbox on the public ticket form to block automated submissions."
            />
            <Box sx={{ mt: 1, p: 1.5, bgcolor: "#F8FAFC", borderRadius: "8px", border: "1px solid #E2E8F0" }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#475467" }}>
                ✓ Server-side honeypot protection is permanently active (hidden fields trap automated spam bots).
                <br />
                ✓ Rate limiting restricts submissions to maximum 15 tickets per IP per hour.
                <br />
                ✓ Dangerous file extensions (.exe, .bat, .sh, .js, .vbs) are automatically rejected by server file-sniffing.
              </Typography>
            </Box>
          </Section>

          <Section title="File Attachments" description="Allow customers and guests to upload screenshots and supporting documents.">
            <Toggle label="Allow file attachments on ticket submission" checked={p.allowAttachments !== false} onChange={setP("allowAttachments")} />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Maximum Files per Ticket"
                  value={p.maxAttachments ?? 5}
                  onChange={(e) => setP("maxAttachments")(Math.max(1, Math.min(20, Number(e.target.value) || 5)))}
                  inputProps={{ min: 1, max: 20 }}
                  helperText="Allowed: 1 to 20 files"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Maximum File Size (MB)"
                  value={p.maxFileSizeMb ?? 20}
                  onChange={(e) => setP("maxFileSizeMb")(Math.max(1, Math.min(100, Number(e.target.value) || 20)))}
                  inputProps={{ min: 1, max: 100 }}
                  helperText="Allowed: 1 to 100 MB per file"
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Ticket Routing & Default Properties" description="Control default category and priority assigned to tickets submitted via the public form.">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Default Ticket Category"
                  value={p.defaultCategoryId || ""}
                  onChange={(e) => setP("defaultCategoryId")(e.target.value || null)}
                >
                  <MenuItem value="">— None (or determined by submitter) —</MenuItem>
                  {ctx.categories?.map((c) => (
                    <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Default Ticket Priority"
                  value={p.defaultPriorityId || ""}
                  onChange={(e) => setP("defaultPriorityId")(e.target.value || null)}
                >
                  <MenuItem value="">— System Default —</MenuItem>
                  {ctx.priorities?.map((pr) => (
                    <MenuItem key={pr._id} value={pr._id}>{pr.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Section>

          <Section title="Knowledge Base & Customer Replies">
            <Toggle label="Search and read knowledge base articles" checked={p.showKnowledgeBase !== false} onChange={setP("showKnowledgeBase")} help="Only published articles with 'Show in customer portal' enabled are visible." />
            <Toggle label="Allow customer replies directly from the portal" checked={p.allowCustomerReplies !== false} onChange={setP("allowCustomerReplies")} help="Customers and guests can post replies and view conversation updates." />
          </Section>
        </Box>
      )}

      {/* TAB 4: CONFIRMATION & SUCCESS */}
      {tab === 4 && (
        <Box>
          <Section title="Submission Confirmation Screen" description="Customize what customers and guests see immediately after submitting a support ticket.">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Success Headline" value={p.successTitle || ""} onChange={(e) => setP("successTitle")(e.target.value)} placeholder="Thanks — we got your request" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" multiline minRows={2} label="Success Message Body" value={p.successMessage || ""} onChange={(e) => setP("successMessage")(e.target.value)} placeholder="Your support request has been created successfully. We will reply to your email shortly." />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2.5, p: 2, bgcolor: "#F8FAFC", borderRadius: "12px", border: "1px dashed #CBD5E1" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#334155", mb: 0.5 }}>Guest Tracking Guarantee:</Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#64748B" }}>
                Guests do not need an account. The confirmation screen displays their unique ticket reference (e.g. <strong>#JW-10001</strong>) and provides a secure, tokenized access link (e.g. <code>/support/{slug || "help"}/tickets/guest/&lt;secure-token&gt;</code>) allowing them to view ticket status, read responses, and reply securely.
              </Typography>
            </Box>
          </Section>
        </Box>
      )}

      {/* FIELD EDIT / ADD DIALOG */}
      {editingField && (
        <Dialog open onClose={() => setEditingField(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>{editingField.isStandard ? "Edit Standard Field" : editingField.id?.startsWith("f_") && fields.some((f) => f.id === editingField.id) ? "Edit Form Field" : "Add Form Field"}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Field Label"
                value={editingField.label || ""}
                onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                required
                helperText="Label shown to the customer (e.g. 'Account Name (Instance Name)')"
              />
              <TextField
                fullWidth
                size="small"
                label="Field Key / Identifier"
                value={editingField.key || ""}
                onChange={(e) => setEditingField({ ...editingField, key: e.target.value.replace(/[^a-zA-Z0-9_]/g, "_") })}
                disabled={editingField.isStandard}
                helperText={editingField.isStandard ? "Standard CRM field key cannot be changed" : "Unique identifier used in CRM ticket customFields (e.g. 'instanceName')"}
              />
              <TextField
                select
                fullWidth
                size="small"
                label="Field Type"
                value={editingField.type || "text"}
                disabled={editingField.isStandard}
                onChange={(e) => setEditingField({ ...editingField, type: e.target.value })}
              >
                {FIELD_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>

              {["select", "multiselect"].includes(editingField.type) && (
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Dropdown Options (comma separated)"
                  value={optionsText}
                  onChange={(e) => setOptionsText(e.target.value)}
                  placeholder="Option 1, Option 2, Option 3"
                  helperText="Enter choices separated by commas"
                />
              )}

              <TextField
                fullWidth
                size="small"
                label="Placeholder Text"
                value={editingField.placeholder || ""}
                onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                placeholder="e.g. 'Select...' or 'Enter value'"
              />

              <TextField
                fullWidth
                size="small"
                label="Help Tooltip / Info Text"
                value={editingField.tooltip || ""}
                onChange={(e) => setEditingField({ ...editingField, tooltip: e.target.value })}
                placeholder="Explains what to enter (shows as (i) icon)"
              />

              <TextField
                select
                fullWidth
                size="small"
                label="Visibility Mode"
                value={editingField.visibility || "all"}
                onChange={(e) => setEditingField({ ...editingField, visibility: e.target.value })}
                helperText="Select which customers see this field"
              >
                <MenuItem value="all">All Users (Guests and Logged-in)</MenuItem>
                <MenuItem value="guest">Guests Only (Hidden from Logged-in)</MenuItem>
                <MenuItem value="authenticated">Logged-in Users Only (Hidden from Guests)</MenuItem>
              </TextField>

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(editingField.required)}
                    onChange={(e) => setEditingField({ ...editingField, required: e.target.checked })}
                  />
                }
                label={<Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>Required Field (Must be filled to submit)</Typography>}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setEditingField(null)} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button variant="contained" sx={primaryButtonSx} onClick={handleSaveField}>Save Field</Button>
          </DialogActions>
        </Dialog>
      )}

      {p.enabled && !slug && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => { setTab(1); setP("slug")("help"); }}>
              Set to '/support/help'
            </Button>
          }
        >
          To activate the customer portal, you must provide a <strong>Portal address</strong> (e.g. <code>help</code>, <code>support</code>, or your company name) in the <strong>Branding &amp; Appearance</strong> tab.
        </Alert>
      )}
      <SaveBar dirty={dirty} saving={saving} onSave={() => save(["portal"])} onReset={reset} />
    </Box>
  );
}
